import { TextField as MuiTextField } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import validator from "validator";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber.js";

const TextField = ({
  slotProps = {},
  startAdornment = null,
  endAdornment = null,
  min = "",
  max = "",
  minLength = 0,
  height = 0,
  validate = "",
  helperText = "",
  useMask = "",
  defaultValue = "",
  color = "#6e6e6e",
  sx = {},
  error = null,
  required = false,
  rounded = false,
  ...otherProps
}) => {
  const [isValid, setIsValid] = useState(!error);
  const [newValue, setNewValue] = useState(defaultValue ?? "");

  const newProps = {
    ...otherProps,
    sx: {
      ...sx,
      color,
      fieldset: {
        borderColor: "currentColor !important",
        borderWidth: "1px !important",
      },
      label: {
        lineHeight: 1,
        overflow: "visible",
      },
    },
    slotProps: {
      formHelperText: {
        sx: {
          lineHeight: 1.4,
          fontSize: 12,
        },
      },
      inputLabel: {
        color: "currentColor !important",
      },
      input: {
        sx: {
          height: height || "fit-content",
          fontSize: 16,
          px: 1.25,
          borderRadius: rounded ? 6 : 1.75,
          color: "currentColor",
        },
        startAdornment,
        endAdornment,
      },
      htmlInput: {
        height: "100%",
        sx: {
          p: 0,
          my: 1.5,
          appearance: "none",
          ...(otherProps.type === "date" && {
            fontFamily: "monospace",
          }),
          color: "#000",
          "&:focused": {
            outline: 0,
          },
          "&:-webkit-autofill, &:autofill": {
            borderRadius: 0,
          },
        },
        min,
        max,
        minLength,
        required,
      },
      ...slotProps,
    },
  };

  const formatValue = (rawValue = "", mask = "") => {
    if (!rawValue) return "";
    const digitsOnly = rawValue.replace(/\D/g, "");

    switch (mask) {
      case "(###) ###-####":
        return formatPhoneNumber(digitsOnly);

      case "#### #### #### ####":
        return digitsOnly.slice(0, 16).replace(/(\d{4})(?=\d)/g, "$1 ");

      case "##/##": {
        if (digitsOnly.length === 0) return "";
        if (digitsOnly.length <= 2) return digitsOnly;

        const month = digitsOnly.slice(0, 2);
        const year = digitsOnly.slice(2, 4);

        return `${month}/${year}`;
      }

      case "####":
        return digitsOnly.slice(0, mask.length);

      default:
        return rawValue;
    }
  };

  const validateField = ({ type, value }) => {
    switch (type) {
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

      case "tel":
        return /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value);

      case "name":
        const array = value.split(" ");

        return (
          array.length >= 2 &&
          array[0].length > 1 &&
          (array[1].length > 1 || array[2]?.length > 1)
        );

      case "zip":
        return value.length >= 5;

      case "cardNumber":
        return validator.isCreditCard(value);
      case "exp":
        const [mm, yy] = value.split("/");

        if (!mm || !yy) return false;

        const dateObj = new Date(+yy + 2000, mm - 1, 15);

        return !!(
          Number(mm) < 13 &&
          value.length === 5 &&
          dateObj.getTime() > new Date().getTime()
        );

      case "cvv":
        return value.length >= 3;

      default:
        return true;
    }
  };

  return (
    <MuiTextField
      value={newValue}
      variant="outlined"
      error={error || !isValid}
      onFocus={() => setIsValid(true)}
      helperText={
        !isValid || error
          ? newValue && helperText
            ? helperText
            : `Must enter ${otherProps.label?.toLowerCase() || "value"}`
          : " "
      }
      onBlur={(e) => {
        const { value, type } = e.target;

        if (validate) {
          const valid = validateField({
            type: validate || type,
            value,
          });
          setIsValid(valid);

          if (!valid) {
            e.target.setCustomValidity(helperText);
            e.target.checkValidity();
          }
        }
      }}
      onChange={(e) => {
        const { value } = e.target;
        if (useMask) {
          setNewValue(formatValue(value, useMask));
        } else {
          setNewValue(value);
        }
      }}
      {...newProps}
    />
  );
};

TextField.propTypes = {
  slotProps: PropTypes.object,
  startAdornment: PropTypes.element,
  endAdornment: PropTypes.element,
  min: PropTypes.string,
  max: PropTypes.string,
  minLength: PropTypes.number,
  height: PropTypes.number,
  validate: PropTypes.string,
  helperText: PropTypes.string,
  useMask: PropTypes.string,
  defaultValue: PropTypes.string,
  color: PropTypes.string,
  sx: PropTypes.object,
  error: PropTypes.bool,
  required: PropTypes.bool,
  rounded: PropTypes.bool,
};

TextField.displayName = "CustomTextField";

export default TextField;
